import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Typography from 'aquilla/core/atoms/Typography';
import { getImage } from '@utils/imageHelper';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import TestSeriesTabSwitcher, {
  TEST_SERIES_TAB
} from './TestSeriesTabSwitcher';
import TestSeriesContentPanel from './TestSeriesContentPanel';

const HERO_BG_DESKTOP = getImage('test-series-listing/hero/hero-bg.svg');
const HERO_BG_MOBILE = getImage('test-series-listing/hero/hero-bg-mobile.svg');

const mobileHeroPadding = `17px ${Spacings.SPACING_4B} ${Spacings.SPACING_6B}`;

const Wrapper = styled.div`
  grid-column: span 18;
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    gap: ${Spacings.SPACING_4B};
    grid-column: span 10;
    width: 100%;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const HeroCard = styled.div`
  border: 1px solid #f1f2f2;
  border-radius: 12px;
  overflow: visible;
  width: 100%;
  box-sizing: border-box;
`;

const HeroSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  background-color: #034eb7;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url(${HERO_BG_DESKTOP});
    background-repeat: no-repeat;
    background-position: top right;
    background-size: cover;
    pointer-events: none;
  }

  ${mediaQuery.uptoMobile} {
    &::before {
      background-image: url(${HERO_BG_MOBILE});
      background-position: top center;
    }
  }

  ${mediaQuery.aboveMobile} {
    gap: ${Spacings.SPACING_6B};
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_8B} 36px;
  }

  ${mediaQuery.uptoMobile} {
    padding: ${mobileHeroPadding};
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  max-width: 640px;

  ${mediaQuery.aboveMobile} {
    gap: ${Spacings.SPACING_6B};
  }

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_6B};
    max-width: none;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_6};
  }
`;

const Title = styled(Typography)`
  color: var(--color-i-white);
  font-size: 28px;
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 1.2;
`;

const MobileTitle = styled(Title)`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: block;
    font-size: 24px;
  }
`;

const DesktopTitle = styled(Title)`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Description = styled(Typography)`
  color: #f1f2f2;
  font-size: 14px;
  line-height: 18px;
  font-weight: ${Fonts.REGULAR};

  ${mediaQuery.uptoMobile} {
    font-size: 16px;
    line-height: 20px;
    white-space: normal;
  }

  ${mediaQuery.aboveMobile} {
    white-space: nowrap;
  }
`;

const ScholarshipSlot = styled.div`
  width: 100%;

  /* DST root uses grid-column; span full TopBar width here */
  & > div {
    grid-column: unset;
    width: 100%;
    max-width: 100%;
  }
`;

const TopBar = ({
  breadcrumbs,
  afterBreadcrumbs,
  title,
  description,
  showTabSwitcher,
  activeTab: activeTabProp,
  onTabChange: onTabChangeProp,
  activeCategoryTab,
  onCategoryTabChange,
  filterConfig,
  appliedSelections,
  onAppliedSelectionsChange,
  showMyTestsTab
}) => {
  const [internalTab, setInternalTab] = useState(TEST_SERIES_TAB.ALL);
  const activeTab = activeTabProp ?? internalTab;
  const onTabChange = onTabChangeProp ?? setInternalTab;

  return (
    <Wrapper>
      {breadcrumbs}
      {afterBreadcrumbs ? (
        <ScholarshipSlot>{afterBreadcrumbs}</ScholarshipSlot>
      ) : null}
      <HeroCard>
        <HeroSection>
          <HeroContent>
            <TextContent>
              <DesktopTitle variant="h3" as="h1">
                {title}
              </DesktopTitle>
              <MobileTitle variant="h3" as="h1">
                {title}
              </MobileTitle>
              {description && (
                <Description variant="p2">{description}</Description>
              )}
            </TextContent>
            {showTabSwitcher && (
              <TestSeriesTabSwitcher
                activeTab={activeTab}
                onTabChange={onTabChange}
                showMyTab={showMyTestsTab}
              />
            )}
          </HeroContent>
        </HeroSection>
        {showTabSwitcher && (
          <TestSeriesContentPanel
            view={activeTab}
            activeCategoryTab={activeCategoryTab}
            onCategoryTabChange={onCategoryTabChange}
            filterConfig={filterConfig}
            appliedSelections={appliedSelections}
            onAppliedSelectionsChange={onAppliedSelectionsChange}
          />
        )}
      </HeroCard>
    </Wrapper>
  );
};

export default TopBar;

TopBar.propTypes = {
  breadcrumbs: PropTypes.node,
  afterBreadcrumbs: PropTypes.node,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  showTabSwitcher: PropTypes.bool,
  activeTab: PropTypes.oneOf([TEST_SERIES_TAB.ALL, TEST_SERIES_TAB.MY]),
  onTabChange: PropTypes.func,
  activeCategoryTab: PropTypes.string.isRequired,
  onCategoryTabChange: PropTypes.func.isRequired,
  filterConfig: PropTypes.arrayOf(PropTypes.object),
  appliedSelections: PropTypes.object.isRequired,
  onAppliedSelectionsChange: PropTypes.func.isRequired,
  showMyTestsTab: PropTypes.bool
};

TopBar.defaultProps = {
  breadcrumbs: null,
  afterBreadcrumbs: null,
  description: '',
  showTabSwitcher: true,
  filterConfig: [],
  showMyTestsTab: true
};
